/**
 * Tool to Generate Combinatorial test cases
 * @param {boolean} isLab default 'false', set to 'true' when used for a lab
 * @param {object} setLabParameters to passing parameters to the lab view in order to validate input
 * @param {boolean} generateDisable default 'false', set to 'true' when used for a lab when the input is not validated
 * @param {function} nextStep function to move to the next step while used as part of lab
 */
import Styles from './CombinatorialTestPane.module.scss'
import { useState, useContext, useRef, useEffect, React } from 'react';
import { ToastContainer, toast } from 'react-toastify';

// Module Imports
import axios from 'axios';
import FeatherIcon from 'feather-icons-react';

// Component Imports
import { ParamsAndConstraints } from './ParamsAndConstraints/ParamsAndConstraints';
import { RequiredTests } from './RequiredTests/RequiredTests';
import { TestScript } from './TestScriptTemplate/TestScript';
import { GeneratedTests } from './GeneratedTests/GeneratedTests';
import { Analysis } from './Analysis/Analysis';
import { AivagamButton } from 'components/global/AivagamButton/AivagamButton';
import { ViewContext } from 'components/lib';

export function CombTestPane({ isLab = false, setLabParameters, generateDisable = false, nextStep, resource }) {
    const viewContext = useContext(ViewContext);
    const [currentView, setCurrentView] = useState('defineParameters');
    const uploadFileRef = useRef(null);

    const [parameters, setParameters] = useState({
        parameters: [],
        constraints: [],
        coverage: 2,
        requiredTests: [],
        testTemplate: '',
        testScript: [],

    });

    function displayError(errMsg) {
        toast.dark(errMsg, {
            type: 'error',
            position: 'top-right',
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });

    }

    useEffect(() => {
        if (isLab) {
            setLabParameters(prevState => ({
                ...prevState,
                parameters: parameters.parameters,
                constraints: parameters.constraints,
                coverage: parameters.coverage,
                requiredTests: parameters.requiredTests,
                testTemplate: parameters.testTemplate
            }))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [parameters])

    function validate_parameters(params) {
        const keys = new Set();
        let validate = true;
        if (!params.length) {
            displayError(viewContext.t('Components.NodeLayout.CombinatorialTestPane.error.parameters.noParams'));
            return validate = false
        }
        // eslint-disable-next-line array-callback-return
        params.map((param) => {
            if (param.key.trim() === '') {
                displayError(viewContext.t('Components.NodeLayout.CombinatorialTestPane.error.parameters.name'));
                return validate = false
            }

            if (keys.has(param.key)) {
                displayError(viewContext.t('Components.NodeLayout.CombinatorialTestPane.error.parameters.duplicates'))
                return validate = false
            }
            keys.add(param.key)

            if (param.values.some(value => value.trim() === '')) {
                displayError(viewContext.t('Components.NodeLayout.CombinatorialTestPane.error.parameters.empty'))
                return validate = false;
            }

        })

        return validate;
    }

    function validate_constraints(constrs) {
        /**
         * function ensure that the constraints are entered in the right format and not empty.
         * when condition is "IF" it should always be accompanied with THEN
         * when condition is "always together" it should always be accompanied with WITH
         */
        for (const constr of constrs) {
            let thenCount = 0;
            let withCount = 0;
            for (const sec of constr.secs) {
                if (sec.param.trim() === '' || sec.value.trim() === '') {
                    displayError(viewContext.t('Components.NodeLayout.CombinatorialTestPane.error.constraints.empty'))
                    // validate = false
                    return false
                }
                if (sec.between === "THEN") {
                    thenCount++;
                }
                if (sec.between === "with") {
                    withCount++;
                }
            }
            if (constr.type === "IF") {
                if (thenCount !== 1 || withCount !== 0) {
                    displayError(viewContext.t('Components.NodeLayout.CombinatorialTestPane.error.constraints.ifBinding'))
                    return false;
                }
            }
            if (constr.type === "always together") {
                if (thenCount !== 0 || withCount !== 1) {
                    displayError(viewContext.t('Components.NodeLayout.CombinatorialTestPane.error.constraints.alwaysBinding'))
                    return false;
                }
            }

        }
        return true
    }

    function validate_requiredTests(requiredTests) {
        const valid = requiredTests.every((item) => {
            if (item.assigns.length === 0) {
                displayError(viewContext.t('Components.NodeLayout.CombinatorialTestPane.error.requiredTests.empty'));
                return false
            }
            return true;
        })

        return valid
    }
    const [generatedTests, setGeneratedTests] = useState()

    const views = [
        {
            label: viewContext.t('Components.NodeLayout.CombinatorialTestPane.label.parameters'),
            value: 'defineParameters'
        },
        {
            label: viewContext.t('Components.NodeLayout.CombinatorialTestPane.label.requiredTests'),
            value: 'requiredTests'
        },
        {
            label: viewContext.t('Components.NodeLayout.CombinatorialTestPane.label.generateTests'),
            value: 'results'
        },
        {
            label: viewContext.t('Components.NodeLayout.CombinatorialTestPane.label.testScript'),
            value: 'testScript'
        },
        {
            label: viewContext.t('Components.NodeLayout.CombinatorialTestPane.label.analysis'),
            value: 'analysis'
        }
    ];

    const handleViewChange = (newValue) => {
        //function allows to move between the views - define parameters, required tests ....
        setCurrentView(newValue)
    };
    
    const endPoint = resource ? 'api/labs/pict' : 'api/pict';
    function handleGenerate() {
        //function validates all the input and sends request to server to generate the test cases
        if (!validate_parameters(parameters.parameters) ||
            !validate_constraints(parameters.constraints) ||
            !validate_requiredTests(parameters.requiredTests)
        ) {
            return;
        }

        const data = {
            parameters: parameters.parameters,
            constraints: parameters.constraints,
            coverage: parameters.coverage,
            required_tests: parameters.requiredTests
        }


        axios.post(endPoint, data)
            .then((response) => {
                setGeneratedTests(response.data.data)
                setCurrentView('results')

                if (response.data.errData) {
                    //to display errors in cases there is any error form pict side
                    displayError(response.data.errData)
                    return;
                }
                if (isLab) {
                    nextStep();
                }
            })
            .catch((error) => {
                viewContext.handleError(error);
            })
    }

    const selectCurrentView = () => {
        switch (currentView) {
            case 'defineParameters':
                return <ParamsAndConstraints
                    parameters={parameters}
                    setParameters={setParameters}
                    generateFunction={handleGenerate}
                    isLab={isLab}
                />
            case 'requiredTests':
                return <RequiredTests
                    parameters={parameters}
                    setParameters={setParameters}
                />
            case 'testScript':
                return <TestScript
                    parameters={parameters}
                    setParameters={setParameters}
                    generatedTests={generatedTests}
                    errorDisplay={displayError}
                />
            case 'results':
                return <GeneratedTests
                    generatedTests={generatedTests}
                    parameters={parameters}
                    setParameters={setParameters}
                    generateFunction={handleGenerate}
                    isLab={isLab}
                    generateDisable={generateDisable}
                    nextStep={nextStep}

                />
            case 'analysis':
                return <Analysis
                    parameters={parameters}
                    setParameters={setParameters}
                    generatedTests={generatedTests}
                    errorDisplay={displayError}
                    resource={resource}
                />
            default:
                return;
        }
    }

    function downloadSuite() {
        /*
            functions validates and sends all the parameters to the server
            where it is formatted and returned to be downloaded as a .txt file
        */
        if (!validate_parameters(parameters.parameters) ||
            !validate_constraints(parameters.constraints) ||
            !validate_requiredTests(parameters.requiredTests)
        ) {
            return;
        }
        axios.post(`${endPoint}/download/suite`, {
            parameters: parameters.parameters,
            constraints: parameters.constraints,
            coverage: parameters.coverage,
            required_tests: parameters.requiredTests,
            test_script_template: parameters.testTemplate,
            generatedTests: generatedTests

        })
            .then(response => {
                const blob = new Blob([response.data], { type: 'text/plain' });
                const url = URL.createObjectURL(blob);

                // Create a temporary <a> element and click it to trigger the download
                const a = document.createElement('a');
                a.href = url;
                a.download = 'Test_Suite.txt';
                a.click();

                // Clean up by revoking the URL
                URL.revokeObjectURL(url);
            })
            .catch(error => {
                viewContext.handleError(error);
            })
    }



    const uploadTestSuite = async (event) => {
        /**
         this function allows to upload the .txt test suite. and converts it into JSON format to display in the appropriate fields
         */
        const file = event.target.files[0];
        const text = await file.text();
        const lines = text.split('\n').map(line => line.trim()).filter(line => line !== '');
        let temp = [];
        let generatedTestsCases = [];
        let jsonData = {
            parameters: [],
            constraints: [],
            coverage: 0,
            requiredTests: [],
            testTemplate: ''
        };
        let section = '';

        lines.forEach(line => {

            if (line.startsWith('#')) {
                section = line.substring(1).toLowerCase();
            } else {
                switch (section) {
                    case 'test parameters':
                        const [key, values] = line.split(':').map(part => part.trim());
                        jsonData.parameters.push({ key, values: values.split(',').map(value => value.trim()) });
                        break;
                    case 'constraints':
                        let sec = {};
                        let secs = []
                        // eslint-disable-next-line no-unused-vars
                        const [type, condition] = line.split('[').map(part => part.trim());
                        const matchParam = line.match(/\[([^\]]+)\]/);
                        const matchValue = line.match(/"([^"]+)"/);
                        sec.param = matchParam[1]
                        sec.value = matchValue[1]
                        sec.bind = line.includes('=') ? "=" : "<>";
                        if (line.includes('THEN')) {
                            sec.between = "THEN";
                        } else if (line.includes('with')) {
                            sec.between = "with";
                        } else if (line.includes('OR')) {
                            sec.between = "OR";
                        } else if (line.includes('AND')) {
                            sec.between = "AND";
                        }
                        secs = [...secs, sec]
                        // Start a new constraint
                        jsonData.constraints.push({ type, secs })
                        break;
                    case 'required tests':
                        const assigns = line.split('\t').map(value => value.trim());
                        jsonData.requiredTests.push({ assigns: assigns.map((value, index) => ({ param: jsonData.parameters[index].key, value })) });
                        break;
                    case 'test script template':
                        temp.push(line)
                        break;
                    case 'generated tests':
                        const rows = line.split('\t');
                        generatedTestsCases.push(rows)
                        break;
                    case 'test coverage':
                        jsonData.coverage = parseInt(line);
                        break;

                    default:
                        break;
                }
            }

        });
        jsonData.generatedTests = {
            headers: generatedTestsCases[0],
            cases: generatedTestsCases.slice(1).map(line => line)
        }

        jsonData.testTemplate = temp.join('\n')
        const formatted_constraints = jsonData.constraints.reduce((acc, curr) => {
            if (curr.type === "") {
                // Merge the `secs` array with the previous object's `secs` array
                acc[acc.length - 1].secs = acc[acc.length - 1].secs.concat(curr.secs);
            } else {
                // Add the current object to the accumulator
                acc.push(curr);
            }
            return acc;
        }, []);
        jsonData.requiredTests.shift()
        // return jsonData;
        setParameters(prevState => {
            return {
                ...prevState,
                parameters: jsonData.parameters,
                constraints: formatted_constraints,
                coverage: parseInt(jsonData.coverage),
                requiredTests: jsonData.requiredTests,
                testTemplate: jsonData.testTemplate
            }
        })
        setGeneratedTests(jsonData.generatedTests)


        event.target.value = null;
    };

    return (
        <div className={Styles.ancestor_container}>
            {!isLab &&
                <ToastContainer
                    id="warning-toast"
                    position="top-right"
                    autoClose={5000}
                    hideProgressBar={false}
                    newestOnTop
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss
                    draggable
                    pauseOnHover
                    style={{ fontSize: "0.8em" }}
                />}
            <div className={Styles.headerContainer}>
                <div className={Styles.tabHeader} style={{ width: isLab && '100%' }}>
                    {
                        views.map((view, index) => (
                            <p
                                key={view.value}
                                style={{
                                    cursor: 'pointer',
                                    backgroundColor: currentView === view.value && 'grey',
                                    color: currentView === view.value && 'white',
                                    // fontWeight: currentView === view.value && 'bold',
                                    transition: 'background-color 0.5s, color 0.5s'
                                }}
                                onClick={() => { handleViewChange(view.value) }}
                            >
                                {view.label}
                            </p>
                        ))
                    }

                </div>
                <span className={Styles.importButtons} style={{ display: isLab && 'none' }}>
                    <input
                        type="file"
                        id="fileInput"
                        ref={uploadFileRef}
                        title={viewContext.t('Components.NodeLayout.CombinatorialTestPane.importCSV')}
                        style={{ display: "none" }}
                        onChange={uploadTestSuite}
                    />
                    <AivagamButton
                        buttonText={
                            <div style={{ display: 'flex', gap: '0.5em', height: '0.8em', fontSize: '0.8em', alignItems: 'center' }}>
                                <FeatherIcon size='1.5em' icon='upload' />
                                {viewContext.t('Components.NodeLayout.CombinatorialTestPane.button.importSuite')}
                            </div>
                        }
                        onClick={() => { uploadFileRef.current.click(); }}
                        classStyles={{ height: 'fit-content' }}

                    />
                    <AivagamButton
                        buttonText={
                            <div style={{ display: 'flex', gap: '0.5em', height: '0.8em', fontSize: '0.8em', alignItems: 'center' }}>
                                <FeatherIcon size='1.5em' icon='save' />
                                {viewContext.t('Components.NodeLayout.CombinatorialTestPane.button.save')}
                            </div>
                        }
                        onClick={downloadSuite}
                        classStyles={{ height: 'fit-content' }}
                        type='confirm'
                    />
                </span>
            </div>
            <div className={Styles.current_view}>
                {selectCurrentView()}
            </div>
        </div>
    );
}

